/**
 * timed-recall.v1 cizici — süreli geri sayım + uygulayıcı Doğru/Yanlış.
 *
 * Her bölüm: prompt ("15 saniye içinde aklına gelen 5 X say.") -> "Başla" ->
 * geri sayım halkası -> süre bitince "Öğrenci doğru cevap verdi mi?" Doğru/Yanlış.
 * Uygulayıci işaretler, sonraki bölüme geçilir. Cocuk ekranla etkilesmez.
 *
 * Icerik: { prompt?, sections:[{id,prompt,seconds,minCount}] }
 * Cevap:  onAnswerChange({ results:{id:bool}, correct:N }); tüm bölümler
 *   işaretlenene kadar null.
 */
import { useEffect, useRef, useState } from "react";
import type { MechanicProps } from "./types";

interface TRSection {
  id: string;
  prompt: string;
  seconds?: number;
  minCount?: number;
}
interface TRContent {
  prompt?: { text?: string };
  sections?: TRSection[];
}

export function TimedRecallItem({ item, onAnswerChange }: MechanicProps) {
  const c = item.content as unknown as TRContent;
  const sections = c.sections ?? [];
  const [ix, setIx] = useState(0);
  const [phase, setPhase] = useState<"ready" | "timing" | "judge">("ready");
  const [remaining, setRemaining] = useState(0);
  const [results, setResults] = useState<Record<string, boolean>>({});

  const sec = sections[ix];
  const total = sec?.seconds ?? 15;

  const cb = useRef(onAnswerChange);
  cb.current = onAnswerChange;
  useEffect(() => {
    const done = sections.length > 0 && sections.every((s) => s.id in results);
    if (done) {
      const correct = sections.filter((s) => results[s.id]).length;
      cb.current({ results, correct });
    } else {
      const correct = sections.filter((s) => results[s.id]).length;
      cb.current(null, Object.keys(results).length > 0 ? { results, correct } : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  // geri sayım
  useEffect(() => {
    if (phase !== "timing") return;
    if (remaining <= 0) {
      setPhase("judge");
      return;
    }
    const t = setTimeout(() => setRemaining((r) => r - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, remaining]);

  function startTimer() {
    setRemaining(total);
    setPhase("timing");
  }
  function mark(ok: boolean) {
    if (!sec) return;
    const id = sec.id;
    setResults((r) => ({ ...r, [id]: ok }));
    if (ix + 1 < sections.length) {
      setIx(ix + 1);
      setPhase("ready");
    } else {
      setPhase("ready"); // hepsi bitti; cevap emit edildi, ileri açık
    }
  }

  if (!sec) return <div className="mech" />;

  const done = sections.every((s) => s.id in results);
  const R = 52;
  const C = 2 * Math.PI * R;

  return (
    <div className="mech tr-mech">
      {c.prompt?.text && <p className="tr-title">{c.prompt.text}</p>}
      <h2 className="prompt">{sec.prompt}</h2>
      <hr className="q-divider" />
      <div className="tr-progress">
        {ix + 1} / {sections.length}
      </div>

      {phase === "ready" && !done && (
        <div className="center-action">
          <button type="button" className="big-action-btn" onClick={startTimer}>
            ▶ Başla ({total} sn)
          </button>
        </div>
      )}

      {phase === "timing" && (
        <div className="center-action">
          {/* halka süre boyunca kesintisiz akar (CSS animasyonu); son 3 saniyede sayı nabız gibi atar */}
          <svg viewBox="0 0 120 120" className="tr-ring" aria-hidden="true">
            <circle cx="60" cy="60" r={R - 7} className="tr-ring-disc" />
            <circle cx="60" cy="60" r={R} className="tr-ring-bg" />
            <circle
              key={`ring-${ix}`}
              cx="60"
              cy="60"
              r={R}
              className="tr-ring-fg"
              transform="rotate(-90 60 60)"
              style={{
                strokeDasharray: C,
                ["--tr-c" as string]: `${C}px`,
                animationDuration: `${total}s`,
              }}
            />
            <text
              key={`num-${remaining}`}
              x="60"
              y="56"
              className={`tr-ring-num${remaining <= 3 ? " pulse" : ""}`}
            >
              {remaining}
            </text>
            <text x="60" y="82" className="tr-ring-unit">
              saniye
            </text>
          </svg>
        </div>
      )}

      {phase === "judge" && (
        <div className="center-action">
          <p className="tr-q">Öğrenci doğru cevap verdi mi?</p>
          <p className="tr-hint">
            Doğru cevap için öğrencinin en az {sec.minCount ?? 5} örnek vermesi gerekir.
          </p>
          <div className="tr-buttons">
            <button type="button" className="tr-btn ok" onClick={() => mark(true)}>
              Doğru
            </button>
            <button type="button" className="tr-btn no" onClick={() => mark(false)}>
              Yanlış
            </button>
          </div>
        </div>
      )}

      {done && phase === "ready" && (
        <div className="center-action">
          <p className="tr-hint">
            {/* doğru sayısı ekranda asla gösterilmez */}
            Tüm bölümler tamamlandı. İlerleyebilirsin.
          </p>
        </div>
      )}
    </div>
  );
}
